import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import type { Candidate } from './AddSelection';
import { catalogAdminKey } from './queries';

export function DestinationPicker({
  value,
  disabled,
  onChange,
}: {
  value: Candidate | null;
  disabled: boolean;
  onChange(value: Candidate | null): void;
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState(value?.label ?? '');
  const [query, setQuery] = useDebouncedState('', 250);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    setInput(value?.label ?? '');
  }, [value?.id, value?.label]);
  const ready = query.length >= 2 && input.trim() === query;
  const results = useQuery({
    queryKey: [...catalogAdminKey, 'order-destinations', query, offset],
    queryFn: async () =>
      (
        await client.get<{ data: Candidate[]; meta: { has_more: boolean } }>('hotel-catalog-admin/candidates/geo', {
          params: { q: query, offset },
        })
      ).data,
    enabled: ready && !disabled,
    retry: false,
  });
  return (
    <Stack spacing={1} sx={{ maxWidth: 500 }}>
      <Autocomplete
        clearOnBlur={false}
        openOnFocus
        value={value}
        inputValue={input}
        options={ready ? (results.data?.data ?? []) : []}
        disabled={disabled}
        filterOptions={(options) => options}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, current) => option.id === current.id}
        loading={ready && results.isFetching}
        loadingText={t('hotelCatalogAdmin.loading')}
        noOptionsText={t(input.trim().length < 2 ? 'hotelDestinationOrder.typeDestination' : 'hotelCatalogAdmin.empty')}
        onInputChange={(_, text, reason) => {
          setInput(text);
          if (reason === 'input' || reason === 'clear') {
            setQuery(text.trim());
            setOffset(0);
          }
        }}
        onChange={(_, next) => {
          setInput(value?.label ?? '');
          setQuery('');
          setOffset(0);
          onChange(next);
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <Stack>
              <Typography variant="body2">{option.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                {option.country_code}
              </Typography>
            </Stack>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={t('hotelDestinationOrder.destination')}
            inputProps={{ ...params.inputProps, maxLength: 128 }}
          />
        )}
      />
      {ready && results.isError && (
        <Alert severity="error">
          {t('hotelCatalogAdmin.loadError')}{' '}
          <Button disabled={disabled} onClick={() => results.refetch()}>
            {t('hotelCatalogAdmin.reload')}
          </Button>
        </Alert>
      )}
      {ready && (offset > 0 || results.data?.meta.has_more) && (
        <Stack direction="row" spacing={1}>
          <Button
            disabled={disabled || results.isFetching || offset === 0}
            onClick={() => setOffset((v) => Math.max(0, v - 25))}
          >
            {t('hotelDestinationOrder.previousMatches')}
          </Button>
          <Button
            disabled={disabled || results.isFetching || !results.data?.meta.has_more || offset >= 10000}
            onClick={() => setOffset((v) => v + 25)}
          >
            {t('hotelDestinationOrder.nextMatches')}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
